import { getElementVisibilityDefaults } from '@/common/elementVisibility'

const getLocalStorageKey = (user_id) => `ElementVisibility:v2:${user_id}`

const normalizeVisibilityMap = (map) => {
    const defaults = getElementVisibilityDefaults()
    if (!map || typeof map !== 'object') {
        return defaults
    }

    const normalized = { ...defaults }
    for (const key of Object.keys(defaults)) {
        if (Object.prototype.hasOwnProperty.call(map, key)) {
            normalized[key] = map[key]
        }
    }
    return normalized
}

export default {
    state: {
        wysiwyg_menu_type: 'bubble',
        dark_mode: false,

        // article settings
        lexile_certified_score_editable: false,
        element_visibility: getElementVisibilityDefaults(),
        element_visibility_initialized: false,

        presets: [],
        presets_loading: false,
        active_preset_id: null,
    },
    mutations: {
        setWYSIWYGMenuType(state, payload) {
            state.wysiwyg_menu_type = payload
        },
        setLexileCertifiedScoreEditable(state, payload) {
            state.lexile_certified_score_editable = payload
        },
        setElementVisibility(state, payload) {
            const { key, value } = payload
            const base = normalizeVisibilityMap(state.element_visibility)
            state.element_visibility = {
                ...base,
                [key]: value,
            }
        },
        setElementVisibilityMap(state, payload) {
            state.element_visibility = normalizeVisibilityMap(payload)
            state.element_visibility_initialized = true
        },
        setElementVisibilityInitialized(state, payload) {
            state.element_visibility_initialized = payload
        },
        setPresets(state, payload) {
            state.presets = payload
        },
        setPresetsLoading(state, payload) {
            state.presets_loading = payload
        },
        setActivePresetId(state, payload) {
            state.active_preset_id = payload
        },
    },
    actions: {
        initElementVisibility({ commit, rootState, state }) {
            if (state.element_visibility_initialized) {
                return
            }
            const user = rootState && rootState.user && rootState.user.user
            if (!user || !user.id) {
                return
            }
            const key = getLocalStorageKey(user.id)
            let parsed = null
            try {
                const raw = localStorage.getItem(key)
                parsed = raw ? JSON.parse(raw) : null
            } catch (error) {
                parsed = null
            }
            commit('setElementVisibilityMap', parsed)
        },
        setElementVisibility({ commit, rootState }, payload) {
            const { key, value } = payload
            commit('setElementVisibility', { key, value })
            const user = rootState && rootState.user && rootState.user.user
            if (!user || !user.id) {
                return
            }
            const storage_key = getLocalStorageKey(user.id)
            const next_map = {
                ...(rootState.settings && rootState.settings.element_visibility
                    ? rootState.settings.element_visibility
                    : {}),
                [key]: value,
            }
            localStorage.setItem(storage_key, JSON.stringify(normalizeVisibilityMap(next_map)))
        },
        setElementVisibilityMap({ commit, rootState }, payload) {
            commit('setElementVisibilityMap', payload)
            const user = rootState && rootState.user && rootState.user.user
            if (!user || !user.id) {
                return
            }
            const storage_key = getLocalStorageKey(user.id)
            localStorage.setItem(storage_key, JSON.stringify(normalizeVisibilityMap(payload)))
        },
        async fetchPresets({ commit, dispatch }) {
            commit('setPresetsLoading', true)
            try {
                const response = await this._vm.axios.get('/api/user_view_filter_presets/publications')
                const presets = response.data && response.data.presets ? response.data.presets : []
                commit('setPresets', presets)
                const active = presets.find(preset => preset.is_active)
                commit('setActivePresetId', active ? active.id : null)
                if (active && active.preset) {
                    dispatch('setElementVisibilityMap', active.preset)
                }
                return presets
            } finally {
                commit('setPresetsLoading', false)
            }
        },
        async applyPreset({ state, commit, dispatch }, presetId) {
            const preset = state.presets.find(item => item.id === presetId)
            if (!preset) {
                return null
            }
            await this._vm.axios.patch(`/api/user_view_filter_presets/${preset.id}/activate`)
            const updated = state.presets.map(item => ({
                ...item,
                is_active: item.id === preset.id,
            }))
            commit('setPresets', updated)
            commit('setActivePresetId', preset.id)
            dispatch('setElementVisibilityMap', preset.preset || {})
            return preset
        },
        async clearActivePreset({ state, commit }, filter_type) {
            await this._vm.axios.patch(`/api/user_view_filter_presets/clear_active/${filter_type}`)
            const updated = state.presets.map(item => ({
                ...item,
                is_active: false,
            }))
            commit('setPresets', updated)
            commit('setActivePresetId', null)
        },
        async createPreset({ state, commit }, payload) {
            const response = await this._vm.axios.post('/api/user_view_filter_presets', payload)
            const created = response.data && response.data.preset ? response.data.preset : null
            if (!created) {
                return null
            }
            const reset = state.presets.map(item => ({ ...item, is_active: false }))
            const merged = [...reset.filter(item => item.id !== created.id), created]
                .sort((a, b) => a.name.localeCompare(b.name))
            commit('setPresets', merged)
            commit('setActivePresetId', created.id)
            return created
        },
        async renamePreset({ state, commit }, { id, name }) {
            const response = await this._vm.axios.patch(`/api/user_view_filter_presets/${id}`, { name })
            const updatedPreset = response.data && response.data.preset ? response.data.preset : null
            if (!updatedPreset) {
                return null
            }
            const updated = state.presets.map(item => (item.id === updatedPreset.id ? updatedPreset : item))
                .sort((a, b) => a.name.localeCompare(b.name))
            commit('setPresets', updated)
            commit('setActivePresetId', updatedPreset.id)
            return updatedPreset
        },
        async deletePreset({ state, commit }, id) {
            await this._vm.axios.delete(`/api/user_view_filter_presets/${id}`)
            const updated = state.presets.filter(item => item.id !== id)
            commit('setPresets', updated)
            const active = updated.find(item => item.is_active)
            commit('setActivePresetId', active ? active.id : null)
            return active || null
        },
    }
}
